import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Car,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  Wrench,
  DollarSign,
  Calendar,
  Upload,
  Camera,
  FileText,
  Download,
  X,
  Bike,
  AlertTriangle,
  RefreshCw,
  Zap
} from 'lucide-react';

const maskPlaca = (value) => {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 7)
    .replace(/^([A-Z]{3})(\d)/, '$1-$2');
};

const statusColors = {
  disponivel: 'bg-green-500',
  alugado: 'bg-red-500',
  manutencao: 'bg-yellow-500',
  vendido: 'bg-gray-500',
  roubado: 'bg-black',
  reservado: 'bg-purple-500'
};

const statusLabels = {
  disponivel: 'Disponível',
  alugado: 'Alugado',
  manutencao: 'Manutenção',
  vendido: 'Vendido',
  roubado: 'Roubado',
  reservado: 'Reservado'
};

export default function VehiclesPage() {
  const { API } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('disponivel'); // Default to disponivel
  const [tipoFilter, setTipoFilter] = useState('todos');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // FIPE states
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [anos, setAnos] = useState([]);
  const [loadingFipe, setLoadingFipe] = useState(false);
  const [fipeResult, setFipeResult] = useState(null);
  const [updatingFipeLote, setUpdatingFipeLote] = useState(false);
  const [updatingFipeSingle, setUpdatingFipeSingle] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Camera states
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    vehicle_id: '',
    marca: '',
    modelo: '',
    ano_modelo: '',
    placa: '',
    renavam: '',
    cor: '',
    tipo: 'moto', // Padrão: moto
    status: 'disponivel',
    km_atual: 0,
    valor_fipe: 0,
    observacoes: '',
    imagem_url: '',
    documento_url: ''
  });
  const [fipeSelection, setFipeSelection] = useState({
    marca_id: '',
    modelo_id: '',
    ano_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // File upload states
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);

  useEffect(() => {
    loadVehicles();
    loadStats();
  }, [search, statusFilter, tipoFilter]);

  useEffect(() => {
    loadSchedulerStatus();
  }, []);

  // Alphanumeric sorting function for vehicle IDs (01, 01.1, 02, 02.1, 03, ...)
  const sortAlphanumeric = (a, b) => {
    const parseId = (id) => {
      if (!id) return [0, 0, ''];
      const idStr = String(id).trim();
      
      // Match patterns like "01", "01.1", "01 pop", "01.1 fan", "05.1 FACTOR", etc
      // Groups: (main number)(optional .subnumber)(optional rest)
      const match = idStr.match(/^(\d+)(?:\.(\d+))?(?:\s+(.*))?$/i);
      if (!match) {
        // If no match, try to extract any leading number
        const numMatch = idStr.match(/^(\d+)/);
        if (numMatch) {
          return [parseInt(numMatch[1], 10), 0, idStr.toLowerCase()];
        }
        return [999999, 0, idStr.toLowerCase()];
      }
      
      const main = parseInt(match[1], 10);
      const sub = match[2] ? parseInt(match[2], 10) : 0;
      const suffix = match[3] ? match[3].toLowerCase().trim() : '';
      
      return [main, sub, suffix];
    };
    
    const [aMain, aSub, aSuffix] = parseId(a.vehicle_id);
    const [bMain, bSub, bSuffix] = parseId(b.vehicle_id);
    
    // First sort by main number
    if (aMain !== bMain) return aMain - bMain;
    
    // Then by sub number (0 if not present, which comes before .1, .2, etc)
    if (aSub !== bSub) return aSub - bSub;
    
    // Finally by suffix alphabetically
    return aSuffix.localeCompare(bSuffix);
  };

  const loadVehicles = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'todos') params.append('status', statusFilter);
      if (tipoFilter !== 'todos') params.append('tipo', tipoFilter);
      
      const response = await axios.get(`${API}/vehicles?${params}`, { withCredentials: true });
      // Sort vehicles alphanumerically by vehicle_id
      const sortedVehicles = response.data.sort(sortAlphanumeric);
      setVehicles(sortedVehicles);
    } catch (error) {
      toast.error('Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/fipe/scheduler/status`, { withCredentials: true });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Erro ao carregar status do scheduler FIPE:', error);
    }
  };

  // FIPE functions
  const loadMarcas = async (tipo) => {
    setLoadingFipe(true);
    try {
      const response = await axios.get(`${API}/fipe/marcas/${tipo}`, { withCredentials: true });
      setMarcas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar marcas');
    } finally {
      setLoadingFipe(false);
    }
  };

  const loadModelos = async (tipo, marcaId) => {
    setLoadingFipe(true);
    try {
      const response = await axios.get(`${API}/fipe/modelos/${tipo}/${marcaId}`, { withCredentials: true });
      setModelos(response.data.modelos || []);
    } catch (error) {
      toast.error('Erro ao carregar modelos');
    } finally {
      setLoadingFipe(false);
    }
  };

  const loadAnos = async (tipo, marcaId, modeloId) => {
    setLoadingFipe(true);
    try {
      const response = await axios.get(`${API}/fipe/anos/${tipo}/${marcaId}/${modeloId}`, { withCredentials: true });
      setAnos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar anos');
    } finally {
      setLoadingFipe(false);
    }
  };

  const loadFipeValue = async (tipo, marcaId, modeloId, anoId) => {
    setLoadingFipe(true);
    try {
      const response = await axios.get(`${API}/fipe/valor/${tipo}/${marcaId}/${modeloId}/${anoId}`, { withCredentials: true });
      const valor = parseFloat(response.data.Valor?.replace('R$ ', '').replace('.', '').replace(',', '.')) || 0;
      setFormData(prev => ({ ...prev, valor_fipe: valor }));
      toast.success(`Valor FIPE: R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`);
    } catch (error) {
      toast.error('Erro ao buscar valor FIPE');
    } finally {
      setLoadingFipe(false);
    }
  };

  // ---- AUTO FIPE (novo) ----
  const buscarFipeAuto = async () => {
    const { tipo, marca, modelo, ano_modelo } = formData;
    if (!marca || !modelo || !ano_modelo) {
      toast.error('Preencha Marca, Modelo e Ano antes de buscar o valor FIPE');
      return;
    }
    setLoadingFipe(true);
    setFipeResult(null);
    try {
      const params = new URLSearchParams({ tipo, marca, modelo, ano: ano_modelo });
      const response = await axios.get(`${API}/fipe/auto?${params}`, { withCredentials: true });
      const data = response.data;
      setFipeResult(data);
      setFormData(prev => ({ ...prev, valor_fipe: data.valor }));
      toast.success(`FIPE encontrada: ${data.valor_str}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Não foi possível localizar na tabela FIPE';
      toast.error(msg);
      setFipeResult({ encontrado: false, mensagem: msg });
    } finally {
      setLoadingFipe(false);
    }
  };

  const atualizarFipeSingle = async (vehicleId) => {
    setUpdatingFipeSingle(vehicleId);
    try {
      const response = await axios.post(`${API}/vehicles/${vehicleId}/fipe/update`, {}, { withCredentials: true });
      toast.success(`FIPE atualizada: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(response.data.valor_fipe)}`);
      loadVehicles();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Não foi possível obter o valor FIPE';
      toast.error(msg);
    } finally {
      setUpdatingFipeSingle(null);
    }
  };

  const atualizarFipeLote = async () => {
    setUpdatingFipeLote(true);
    try {
      const response = await axios.post(`${API}/vehicles/fipe/update-all`, {}, { withCredentials: true });
      const { total, atualizados, nao_encontrados } = response.data;
      toast.success(`FIPE em lote: ${atualizados}/${total} veículos atualizados. ${nao_encontrados} não encontrados.`);
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao atualizar FIPE em lote');
    } finally {
      setUpdatingFipeLote(false);
    }
  };

  // Image upload functions
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedVehicle) {
      toast.error('Salve o veículo primeiro para enviar imagens');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPEG, PNG ou WebP.');
      return;
    }
    
    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/vehicles/${selectedVehicle.vehicle_id}/upload-image`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData(prev => ({ ...prev, imagem_url: response.data.imagem_url }));
      toast.success('Imagem enviada com sucesso!');
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setShowCameraModal(true);
    } catch (error) {
      toast.error('Não foi possível acessar a câmera');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCapturedImage(null);
    setShowCameraModal(false);
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedImage(imageData);
    }
  };

  const saveCapture = async () => {
    if (!capturedImage || !selectedVehicle) {
      toast.error('Salve o veículo primeiro para enviar imagens');
      return;
    }
    
    setUploadingImage(true);
    try {
      const response = await axios.post(
        `${API}/vehicles/${selectedVehicle.vehicle_id}/upload-image-base64`,
        { image_data: capturedImage },
        { withCredentials: true }
      );
      setFormData(prev => ({ ...prev, imagem_url: response.data.imagem_url }));
      toast.success('Foto capturada com sucesso!');
      stopCamera();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar foto');
    } finally {
      setUploadingImage(false);
    }
  };

  // Document upload functions
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!selectedVehicle) {
      toast.error('Salve o veículo primeiro para enviar documentos');
      return;
    }
    
    if (file.type !== 'application/pdf') {
      toast.error('Tipo de arquivo não permitido. Use apenas PDF.');
      return;
    }
    
    setUploadingDocument(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/vehicles/${selectedVehicle.vehicle_id}/upload-document`,
        formDataUpload,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData(prev => ({ ...prev, documento_url: response.data.documento_url }));
      toast.success('Documento enviado com sucesso!');
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setUploadingDocument(false);
    }
  };

  const downloadDocument = async (vehicle) => {
    if (!vehicle.documento_url) {
      toast.error('Este veículo não possui documento');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/vehicles/${vehicle.vehicle_id}/download-document`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documento_${vehicle.vehicle_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const deleteImage = async () => {
    if (!selectedVehicle) return;
    
    try {
      await axios.delete(`${API}/vehicles/${selectedVehicle.vehicle_id}/image`, { withCredentials: true });
      setFormData(prev => ({ ...prev, imagem_url: '' }));
      toast.success('Imagem removida com sucesso!');
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao remover imagem');
    }
  };

  const deleteDocument = async () => {
    if (!selectedVehicle) return;
    
    try {
      await axios.delete(`${API}/vehicles/${selectedVehicle.vehicle_id}/document`, { withCredentials: true });
      setFormData(prev => ({ ...prev, documento_url: '' }));
      toast.success('Documento removido com sucesso!');
      loadVehicles();
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (formMode === 'create') {
        const response = await axios.post(`${API}/vehicles`, formData, { withCredentials: true });
        toast.success('Veículo cadastrado com sucesso!');
        // Set selected vehicle for file uploads
        setSelectedVehicle(response.data);
        setFormMode('edit');
      } else {
        await axios.put(`${API}/vehicles/${selectedVehicle.vehicle_id}`, formData, { withCredentials: true });
        toast.success('Veículo atualizado com sucesso!');
        setShowModal(false);
        resetForm();
      }
      loadVehicles();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar veículo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/vehicles/${selectedVehicle.vehicle_id}`, { withCredentials: true });
      toast.success('Veículo excluído com sucesso!');
      setShowDeleteDialog(false);
      loadVehicles();
      loadStats();
    } catch (error) {
      toast.error('Erro ao excluir veículo');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      marca: '',
      modelo: '',
      ano_modelo: '',
      placa: '',
      renavam: '',
      cor: '',
      tipo: 'moto', // Padrão: moto
      status: 'disponivel',
      km_atual: 0,
      valor_fipe: 0,
      observacoes: '',
      imagem_url: '',
      documento_url: ''
    });
    setFipeSelection({ marca_id: '', modelo_id: '', ano_id: '' });
    setMarcas([]);
    setModelos([]);
    setAnos([]);
    setFipeResult(null);
    setSelectedVehicle(null);
  };

  const openCreateModal = () => {
    setFormMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (vehicle) => {
    setFormMode('edit');
    setSelectedVehicle(vehicle);
    setFormData({
      vehicle_id: vehicle.vehicle_id,
      marca: vehicle.marca,
      modelo: vehicle.modelo,
      ano_modelo: vehicle.ano_modelo,
      placa: vehicle.placa,
      renavam: vehicle.renavam,
      cor: vehicle.cor,
      tipo: vehicle.tipo,
      status: vehicle.status,
      km_atual: vehicle.km_atual,
      valor_fipe: vehicle.valor_fipe,
      observacoes: vehicle.observacoes,
      imagem_url: vehicle.imagem_url,
      documento_url: vehicle.documento_url
    });
    setShowModal(true);
  };

  const openViewModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowViewModal(true);
  };

  const openDeleteDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowDeleteDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Encode spaces and special characters in the URL path
    // The uploads are served from /api/uploads
    const encodedUrl = url.split('/').map(part => encodeURIComponent(part)).join('/');
    const apiUrl = encodedUrl.replace('/uploads/', '/api/uploads/');
    return `${API.replace('/api', '')}${apiUrl}`;
  };

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Veículos</h1>
          <p className="text-gray-500 text-sm">Gerencie a frota da locadora</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button
            variant="outline"
            onClick={atualizarFipeLote}
            disabled={updatingFipeLote}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="fipe-lote-button"
          >
            {updatingFipeLote ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2" />Atualizando...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" />Atualizar FIPE em Lote</>
            )}
          </Button>
          <Button onClick={openCreateModal} data-testid="add-vehicle-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Veículo
          </Button>
        </div>
      </div>

      {/* FIPE Scheduler Status */}
      {schedulerStatus && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm" data-testid="fipe-scheduler-panel">
          <div className="flex items-center gap-2 text-green-700 shrink-0">
            <div className={`w-2 h-2 rounded-full ${schedulerStatus.scheduler_ativo ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <span className="font-semibold">Rotina FIPE Automática</span>
            <span className={`text-xs px-1.5 py-0.5 rounded ${schedulerStatus.scheduler_ativo ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
              {schedulerStatus.scheduler_ativo ? 'Ativa' : 'Inativa'}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 text-gray-600 text-xs flex-1">
            {schedulerStatus.proximas_execucoes?.map((job) => (
              <span key={job.job_id} className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-green-500" />
                <strong>{job.nome.replace('Atualização FIPE - ', '')}:</strong> {job.proxima_execucao_br || '—'}
              </span>
            ))}
            {schedulerStatus.ultimas_execucoes?.length > 0 && (
              <span className="flex items-center gap-1 text-gray-500">
                <span>Última: {schedulerStatus.ultimas_execucoes[0].executado_em_br} ({schedulerStatus.ultimas_execucoes[0].atualizados} atualizados)</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'disponivel' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setStatusFilter('disponivel')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.disponiveis || 0}</p>
            <p className="text-xs text-gray-500">Disponíveis</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'alugado' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setStatusFilter('alugado')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-4 h-4 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.alugados || 0}</p>
            <p className="text-xs text-gray-500">Alugados</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'reservado' ? 'ring-2 ring-purple-500' : ''}`} onClick={() => setStatusFilter('reservado')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-600">{stats.reservados || 0}</p>
            <p className="text-xs text-gray-500">Reservados</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'manutencao' ? 'ring-2 ring-yellow-500' : ''}`} onClick={() => setStatusFilter('manutencao')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-yellow-100 flex items-center justify-center">
              <Wrench className="w-4 h-4 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-yellow-600">{stats.manutencao || 0}</p>
            <p className="text-xs text-gray-500">Manutenção</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'vendido' ? 'ring-2 ring-gray-500' : ''}`} onClick={() => setStatusFilter('vendido')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-gray-600" />
            </div>
            <p className="text-2xl font-bold text-gray-600">{stats.vendidos || 0}</p>
            <p className="text-xs text-gray-500">Vendidos</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'todos' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setStatusFilter('todos')}>
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
              <Car className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.total || 0}</p>
            <p className="text-xs text-gray-500">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por ID, marca, modelo ou placa..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-vehicles"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="disponivel">Disponível</SelectItem>
                <SelectItem value="alugado">Alugado</SelectItem>
                <SelectItem value="reservado">Reservado</SelectItem>
                <SelectItem value="manutencao">Manutenção</SelectItem>
                <SelectItem value="vendido">Vendido</SelectItem>
                <SelectItem value="roubado">Roubado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-[150px]" data-testid="tipo-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="carro">Carro</SelectItem>
                <SelectItem value="moto">Moto</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : vehicles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Car className="w-12 h-12 mb-2 opacity-50" />
            <p>Nenhum veículo encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {vehicles.map((vehicle) => (
            <Card 
              key={vehicle.vehicle_id} 
              className="vehicle-card overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`vehicle-card-${vehicle.vehicle_id}`}
            >
              {/* Status Banner */}
              <div className={`h-2 ${statusColors[vehicle.status]}`}></div>
              
              <CardContent className="p-4">
                {/* Image placeholder */}
                <div className="aspect-video bg-gray-100 rounded-lg mb-4 flex items-center justify-center overflow-hidden relative group">
                  {vehicle.imagem_url && !imageErrors[vehicle.vehicle_id] ? (
                    <img 
                      src={getImageUrl(vehicle.imagem_url)} 
                      alt={`${vehicle.marca} ${vehicle.modelo}`}
                      className="w-full h-full object-cover"
                      onError={() => setImageErrors((prev) => ({ ...prev, [vehicle.vehicle_id]: true }))}
                    />
                  ) : (
                    <Car className="w-12 h-12 text-gray-300" />
                  )}
                  {/* Document download overlay */}
                  {vehicle.documento_url && (
                    <div className="absolute top-2 right-2">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-white"
                        onClick={(e) => { e.stopPropagation(); downloadDocument(vehicle); }}
                        data-testid={`download-doc-${vehicle.vehicle_id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        PDF
                      </Button>
                    </div>
                  )}
                </div>

                {/* Vehicle Info */}
                <div className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{vehicle.marca} {vehicle.modelo}</p>
                      <p className="text-sm text-gray-500">{vehicle.ano_modelo}</p>
                    </div>
                    <Badge className={`${statusColors[vehicle.status]} text-white`}>
                      {statusLabels[vehicle.status]}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">ID</p>
                      <p className="font-mono">{vehicle.vehicle_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Placa</p>
                      <p className="font-semibold">{vehicle.placa}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">RENAVAM</p>
                      <p className="font-mono text-xs">{vehicle.renavam}</p>
                    </div>
                  <div>
                      <p className="text-gray-500">Valor FIPE</p>
                      <div className="flex items-center gap-1">
                        <p className={`font-semibold ${vehicle.valor_fipe > 0 ? 'text-green-600' : 'text-red-400'}`}>
                          {formatCurrency(vehicle.valor_fipe)}
                        </p>
                        {vehicle.valor_fipe === 0 && (
                          <span className="text-xs text-red-400 font-normal">(sem valor)</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Reserved info */}
                  {vehicle.status === 'reservado' && vehicle.reserved_by && (
                    <div className="mt-2 p-2 bg-purple-50 rounded-lg text-sm">
                      <p className="text-purple-700">
                        <strong>Reservado para:</strong> {vehicle.reserved_by}
                      </p>
                      <p className="text-purple-600 text-xs">
                        Até: {vehicle.reserved_until}
                      </p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Atualizar FIPE"
                    onClick={() => atualizarFipeSingle(vehicle.vehicle_id)}
                    disabled={updatingFipeSingle === vehicle.vehicle_id}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                    data-testid={`fipe-update-${vehicle.vehicle_id}`}
                  >
                    {updatingFipeSingle === vehicle.vehicle_id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openViewModal(vehicle)}
                    data-testid={`view-vehicle-${vehicle.vehicle_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(vehicle)}
                    data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteDialog(vehicle)}
                    className="text-red-600 hover:text-red-700"
                    data-testid={`delete-vehicle-${vehicle.vehicle_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal - New Design */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) { setShowModal(false); resetForm(); } else { setShowModal(true); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {formMode === 'create' ? 'Novo Veículo' : 'Editar Veículo'}
            </DialogTitle>
            <p className="text-sm text-gray-500">
              {formMode === 'create' 
                ? 'Preencha os campos abaixo para cadastrar um novo veículo na frota.' 
                : 'Atualize as informações do veículo.'}
            </p>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section: Tipo de Veículo (NO TOPO) */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b pb-2">
                Tipo de Veículo
              </h3>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.tipo === 'moto' ? 'default' : 'outline'}
                  className={`flex-1 ${formData.tipo === 'moto' ? 'bg-blue-600' : ''}`}
                  onClick={() => setFormData({ ...formData, tipo: 'moto' })}
                  data-testid="vehicle-tipo-moto"
                >
                  <Bike className="w-4 h-4 mr-2" />
                  Moto
                </Button>
                <Button
                  type="button"
                  variant={formData.tipo === 'carro' ? 'default' : 'outline'}
                  className={`flex-1 ${formData.tipo === 'carro' ? 'bg-blue-600' : ''}`}
                  onClick={() => setFormData({ ...formData, tipo: 'carro' })}
                  data-testid="vehicle-tipo-carro"
                >
                  <Car className="w-4 h-4 mr-2" />
                  Carro
                </Button>
              </div>
            </div>

            {/* Section: Dados do Veículo */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b pb-2">
                Dados do Veículo
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vehicle_id">ID Veículo *</Label>
                  <Input
                    id="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                    placeholder="Ex: VEH001"
                    required
                    disabled={formMode === 'edit'}
                    data-testid="vehicle-id-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="marca">Marca *</Label>
                  <Input
                    id="marca"
                    value={formData.marca}
                    onChange={(e) => setFormData({ ...formData, marca: e.target.value.toUpperCase() })}
                    placeholder="Ex: HONDA, YAMAHA, VOLKSWAGEN"
                    required
                    data-testid="vehicle-marca-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="modelo">Modelo *</Label>
                  <Input
                    id="modelo"
                    value={formData.modelo}
                    onChange={(e) => setFormData({ ...formData, modelo: e.target.value.toUpperCase() })}
                    placeholder="Ex: CG 160, GOL 1.0"
                    required
                    data-testid="vehicle-modelo-input"
                  />
                </div>

                <div>
                  <Label htmlFor="ano_modelo">Ano/Modelo *</Label>
                  <Input
                    id="ano_modelo"
                    value={formData.ano_modelo}
                    onChange={(e) => setFormData({ ...formData, ano_modelo: e.target.value })}
                    placeholder="Ex: 2020/2021"
                    required
                    data-testid="vehicle-ano-input"
                  />
                </div>

                <div>
                  <Label htmlFor="placa">Placa *</Label>
                  <Input
                    id="placa"
                    value={formData.placa}
                    onChange={(e) => setFormData({ ...formData, placa: maskPlaca(e.target.value) })}
                    placeholder="ABC-1234"
                    required
                    disabled={formMode === 'edit'}
                    data-testid="vehicle-placa-input"
                  />
                </div>

                <div>
                  <Label htmlFor="renavam">RENAVAM *</Label>
                  <Input
                    id="renavam"
                    value={formData.renavam}
                    onChange={(e) => setFormData({ ...formData, renavam: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                    placeholder="Digite o RENAVAM"
                    required
                    disabled={formMode === 'edit'}
                    data-testid="vehicle-renavam-input"
                  />
                </div>

                <div>
                  <Label htmlFor="cor">Cor *</Label>
                  <Input
                    id="cor"
                    value={formData.cor}
                    onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                    placeholder="Ex: Branco, Preto, Prata"
                    required
                    data-testid="vehicle-cor-input"
                  />
                </div>

                {/* Status */}
                <div className="md:col-span-2">
                  <Label>Status</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        variant={formData.status === value ? 'default' : 'outline'}
                        size="sm"
                        className={formData.status === value ? statusColors[value] : ''}
                        onClick={() => setFormData({ ...formData, status: value })}
                        data-testid={`vehicle-status-${value}`}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Section: Observações */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b pb-2">
                Observações
              </h3>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Descreva informações importantes sobre este veículo..."
                rows={3}
                data-testid="vehicle-obs-input"
              />
            </div>

            {/* Section: Valores e KM */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b pb-2">
                Valores e KM
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="km_atual">KM Atual</Label>
                  <Input
                    id="km_atual"
                    type="number"
                    value={formData.km_atual}
                    onChange={(e) => setFormData({ ...formData, km_atual: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    data-testid="vehicle-km-input"
                  />
                </div>
                <div>
                  <Label htmlFor="valor_fipe">Valor Tabela FIPE (R$)</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        id="valor_fipe"
                        type="number"
                        step="0.01"
                        value={formData.valor_fipe}
                        onChange={(e) => setFormData({ ...formData, valor_fipe: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        data-testid="vehicle-fipe-input"
                      />
                      {loadingFipe && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={buscarFipeAuto}
                      disabled={loadingFipe || !formData.marca || !formData.modelo || !formData.ano_modelo}
                      className="shrink-0 border-green-300 text-green-700 hover:bg-green-50 px-3"
                      data-testid="buscar-fipe-button"
                      title="Buscar valor FIPE automaticamente (preencha Marca, Modelo e Ano primeiro)"
                    >
                      {loadingFipe ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                      ) : (
                        <><Zap className="w-4 h-4 mr-1" />FIPE</>
                      )}
                    </Button>
                  </div>
                  {/* FIPE result feedback */}
                  {fipeResult && (
                    <div className={`mt-2 p-2 rounded text-xs leading-relaxed ${fipeResult.encontrado !== false ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}
                      data-testid="fipe-result-feedback"
                    >
                      {fipeResult.encontrado !== false ? (
                        <span>
                          <strong>{fipeResult.valor_str}</strong> · {fipeResult.marca_fipe} {fipeResult.modelo_fipe} {fipeResult.ano_fipe}
                          {fipeResult.combustivel && <> · {fipeResult.combustivel}</>}
                        </span>
                      ) : (
                        <span>{fipeResult.mensagem}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section: Imagens e Documentos */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider border-b pb-2">
                Imagens e Documentos
              </h3>
              
              {formMode === 'create' && !selectedVehicle && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Salve o veículo primeiro para poder enviar imagens e documentos.
                </p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Image Upload */}
                <div>
                  <Label>Foto de Identificação</Label>
                  <div className="mt-2">
                    {formData.imagem_url ? (
                      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <img 
                          src={getImageUrl(formData.imagem_url)} 
                          alt="Veículo"
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={deleteImage}
                          disabled={!selectedVehicle}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          ref={imageInputRef}
                          onChange={handleImageUpload}
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          data-testid="vehicle-image-input"
                        />
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 mb-3">Clique para enviar ou arraste</p>
                        <div className="flex gap-2 justify-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => imageInputRef.current?.click()}
                            disabled={!selectedVehicle || uploadingImage}
                            data-testid="upload-image-btn"
                          >
                            {uploadingImage ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Arquivo
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={startCamera}
                            disabled={!selectedVehicle}
                            data-testid="camera-btn"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Câmera
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Upload */}
                <div>
                  <Label>Documento do Veículo (PDF)</Label>
                  <div className="mt-2">
                    {formData.documento_url ? (
                      <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-red-500" />
                          <div>
                            <p className="font-medium text-sm">Documento PDF</p>
                            <p className="text-xs text-gray-500">Clique para baixar</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDocument(selectedVehicle)}
                            data-testid="download-document-btn"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={deleteDocument}
                            disabled={!selectedVehicle}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          ref={documentInputRef}
                          onChange={handleDocumentUpload}
                          accept="application/pdf"
                          className="hidden"
                          data-testid="vehicle-document-input"
                        />
                        <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 mb-3">Clique para enviar ou arraste</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => documentInputRef.current?.click()}
                          disabled={!selectedVehicle || uploadingDocument}
                          data-testid="upload-document-btn"
                        >
                          {uploadingDocument ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          Enviar PDF
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="save-vehicle-button">
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                {formMode === 'create' ? 'Salvar Veículo' : 'Atualizar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Camera Modal */}
      <Dialog open={showCameraModal} onOpenChange={(open) => { if (!open) stopCamera(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Capturar Foto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!capturedImage ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <img src={capturedImage} alt="Captura" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex justify-center gap-4">
              {!capturedImage ? (
                <>
                  <Button variant="outline" onClick={stopCamera}>Cancelar</Button>
                  <Button onClick={capturePhoto} data-testid="capture-photo-btn">
                    <Camera className="w-4 h-4 mr-2" />
                    Capturar
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setCapturedImage(null)}>
                    Nova Foto
                  </Button>
                  <Button onClick={saveCapture} disabled={uploadingImage} data-testid="save-capture-btn">
                    {uploadingImage ? 'Salvando...' : 'Usar Foto'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Veículo</DialogTitle>
          </DialogHeader>
          {selectedVehicle && (
            <div className="space-y-4">
              {/* Image */}
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                {selectedVehicle.imagem_url ? (
                  <img 
                    src={getImageUrl(selectedVehicle.imagem_url)} 
                    alt={`${selectedVehicle.marca} ${selectedVehicle.modelo}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Car className="w-16 h-16 text-gray-300" />
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{selectedVehicle.marca} {selectedVehicle.modelo}</h3>
                  <p className="text-sm text-gray-500">{selectedVehicle.ano_modelo}</p>
                </div>
                <Badge className={`${statusColors[selectedVehicle.status]} text-white`}>
                  {statusLabels[selectedVehicle.status]}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">ID</p>
                  <p className="font-mono font-medium">{selectedVehicle.vehicle_id}</p>
                </div>
                <div>
                  <p className="text-gray-500">Placa</p>
                  <p className="font-semibold">{selectedVehicle.placa}</p>
                </div>
                <div>
                  <p className="text-gray-500">RENAVAM</p>
                  <p className="font-mono">{selectedVehicle.renavam}</p>
                </div>
                <div>
                  <p className="text-gray-500">Cor</p>
                  <p className="font-medium">{selectedVehicle.cor}</p>
                </div>
                <div>
                  <p className="text-gray-500">KM Atual</p>
                  <p className="font-medium">{selectedVehicle.km_atual?.toLocaleString('pt-BR')} km</p>
                </div>
                <div>
                  <p className="text-gray-500">Valor FIPE</p>
                  <p className="font-semibold text-green-600">{formatCurrency(selectedVehicle.valor_fipe)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Tipo</p>
                  <p className="font-medium capitalize">{selectedVehicle.tipo}</p>
                </div>
                {selectedVehicle.documento_url && (
                  <div>
                    <p className="text-gray-500">Documento</p>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="p-0 h-auto"
                      onClick={() => downloadDocument(selectedVehicle)}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Baixar PDF
                    </Button>
                  </div>
                )}
              </div>

              {selectedVehicle.observacoes && (
                <div>
                  <p className="text-gray-500 text-sm">Observações</p>
                  <p className="text-sm">{selectedVehicle.observacoes}</p>
                </div>
              )}

              {/* Status History */}
              {selectedVehicle.status_history?.length > 0 && (
                <div>
                  <p className="text-gray-500 text-sm mb-2">Histórico de Status</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedVehicle.status_history.slice().reverse().slice(0, 5).map((entry, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-gray-50 p-2 rounded">
                        <Badge className={`${statusColors[entry.status]} text-white text-xs`}>
                          {statusLabels[entry.status]}
                        </Badge>
                        <span className="text-gray-500">
                          {new Date(entry.date).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => {
                  setShowViewModal(false);
                  openEditModal(selectedVehicle);
                }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o veículo "{selectedVehicle?.marca} {selectedVehicle?.modelo} - {selectedVehicle?.placa}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-vehicle"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
